import { reactive } from 'vue'

export interface ConfirmOptions {
  /** Bold heading. Falls back to a generic "Confirmare" if omitted. */
  title?: string
  /** Body text / question. */
  message: string
  /** Confirm button label. Defaults to common.confirm. */
  confirmText?: string
  /** Cancel button label. Defaults to common.cancel. */
  cancelText?: string
  /** Visual style of the confirm button. 'danger' (red) for destructive actions. */
  variant?: 'danger' | 'primary'
}

interface ConfirmState extends Required<Omit<ConfirmOptions, 'title' | 'confirmText' | 'cancelText'>> {
  isOpen: boolean
  title?: string
  confirmText?: string
  cancelText?: string
}

// Single shared dialog state — rendered once by <ConfirmDialog /> mounted in App.vue.
const state = reactive<ConfirmState>({
  isOpen: false,
  title: undefined,
  message: '',
  confirmText: undefined,
  cancelText: undefined,
  variant: 'danger'
})

let resolver: ((value: boolean) => void) | null = null

function settle(value: boolean) {
  state.isOpen = false
  const resolve = resolver
  resolver = null
  resolve?.(value)
}

/**
 * Promise-based confirmation dialog replacing native window.confirm().
 *
 *   const ok = await confirm({ message: '...', variant: 'danger' })
 *   if (!ok) return
 */
export function useConfirm() {
  function confirm(options: ConfirmOptions): Promise<boolean> {
    // If a previous dialog is somehow still pending, resolve it as cancelled.
    if (resolver) settle(false)

    state.title = options.title
    state.message = options.message
    state.confirmText = options.confirmText
    state.cancelText = options.cancelText
    state.variant = options.variant ?? 'danger'
    state.isOpen = true

    return new Promise<boolean>((resolve) => {
      resolver = resolve
    })
  }

  return {
    state,
    confirm,
    accept: () => settle(true),
    cancel: () => settle(false)
  }
}
